import React, { useEffect, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import axios from 'axios'
import PageFrame from '../../QuickMtcProposal/modules/PageFrame'
import useTheme from '../../QuickMtcProposal/hooks/useTheme'
import '../../QuickMtcProposal/styles/proposal.css'
import { VARIABLE_DEFINITIONS } from '../../QuickMtcProposal/utils/proposalVariables'

// Fallback themes — only used if the server can't find the MTC template
const FALLBACK_THEMES = {
  hmi: {
    colorPrimary: '#1b3a5c', colorAccent: '#2ea3f2', colorGold: '#d4a84b',
    colorBackground: '#f7f8fa', colorText: '#333333', colorMuted: '#666666',
    fontDisplay: 'Cormorant Garamond', fontBody: 'DM Sans',
    logoUrl: '/640a56404965b71a9edd0e7d_Logo.png', logoWidth: 180
  },
  bhl: {
    colorPrimary: '#2c4a1e', colorAccent: '#5a8f3d', colorGold: '#d4a843',
    colorBackground: '#f7f5f0', colorText: '#2a2a2a', colorMuted: '#7a7a6e',
    fontDisplay: 'Cormorant Garamond', fontBody: 'DM Sans',
    logoUrl: '/610a290f45ef7427f94b2e78_Logo.png', logoWidth: 160
  },
  dfl: {
    colorPrimary: '#1e3a5f', colorAccent: '#4a7c59', colorGold: '#c9973e',
    colorBackground: '#f4f2ed', colorText: '#2a2a2a', colorMuted: '#85807a',
    fontDisplay: 'Cormorant Garamond', fontBody: 'DM Sans',
    logoUrl: '/61088b311393fa51ac7e1b1b_Logo.png', logoWidth: 160
  }
}

const ENTITY_NAME_MAP = {
  hmi: 'Handyman Maintenance, Inc.',
  bhl: 'Bee Happy Lawns, LLC',
  dfl: 'Desert Foothills Landscaping, LLC'
}

function getFallbackTheme (entity) {
  if (!entity || typeof entity !== 'object') return FALLBACK_THEMES.hmi
  const name = (entity.name || entity.internalName || '').toLowerCase().trim()
  return FALLBACK_THEMES[name] || FALLBACK_THEMES.hmi
}

function getEntityKey (entity) {
  if (!entity || typeof entity !== 'object') return 'hmi'
  return (entity.name || entity.internalName || 'hmi').toLowerCase().trim()
}

// Variable interpolation with currency/select awareness
function interpolate (text, variables) {
  if (!text || !variables) return text || ''
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (variables[key] == null) return match
    const val = variables[key]
    const def = VARIABLE_DEFINITIONS.find(d => d.key === key)
    if (def && def.type === 'currency') {
      return '$' + Number(val).toFixed(2)
    }
    if (def && def.type === 'select' && def.options) {
      const opt = def.options.find(o => o.value === val)
      if (opt) return opt.label
    }
    return String(val)
  })
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0)
}

const getTaxRateDisplay = (estimate) => {
  if (!estimate.taxLocales || !estimate.taxLocales.length) return ''
  const rates = estimate.taxLocales.map(l => parseFloat(l.rate) || 0).filter(r => r > 0)
  if (rates.length === 0) return ''
  const totalRate = rates.reduce((sum, r) => sum + r, 0)
  return `${(totalRate * 100).toFixed(2)}%`
}

const formatDate = (d) => {
  if (!d) return ''
  const dt = new Date(d)
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

/* ── Cover Page ───────────────────────────────────────────── */
const CoverPage = ({ theme, estimate, client, salesRepName, entityNameFormal }) => (
  <div className="proposal-page proposal-page--fixed">
    <div className="proposal-cover">
      <div className="proposal-cover__accent-bar" />
      <div className="proposal-cover__body">
        {theme?.logoUrl && (
          <div className="proposal-cover__logo">
            <img src={theme.logoUrl} alt="Company Logo" style={{ maxWidth: theme?.logoWidth || 160 }} />
          </div>
        )}
        <div className="proposal-cover__subtitle">
          {estimate.name || 'Landscape Services Proposal'}
        </div>
        <div className="proposal-cover__divider" />
        <div className="proposal-cover__title">
          Estimate #{estimate.number || ''}
        </div>
        <div style={{ height: 48 }} />
        <div className="proposal-cover__client">
          <div className="proposal-cover__client-name">
            {client?.displayName || client?.firstName || 'Valued Client'}
          </div>
          {salesRepName && <div>Prepared by {salesRepName}</div>}
          {estimate.created_at && <div>{formatDate(estimate.created_at)}</div>}
        </div>
      </div>
      <div className="proposal-cover__footer">
        {entityNameFormal || ''}
      </div>
    </div>
  </div>
)

/* ── Introduction Page ────────────────────────────────────── */
const IntroductionPage = ({ theme, estimate, entityNameFormal, salesRepName, pageNumber, totalPages }) => {
  const hasContent = estimate.introduction || estimate.objective || (estimate.objectives && estimate.objectives.length) || estimate.areas
  if (!hasContent) return null

  return (
    <div className="proposal-page proposal-page--flow">
      <PageFrame theme={theme} pageNumber={pageNumber} totalPages={totalPages} entityNameFormal={entityNameFormal} headerTitle="Landscape Proposal">
        <div className="proposal-section">
          <div className="proposal-section__headline">Introduction</div>
          <div className="proposal-section__accent-line" />

          {estimate.introduction && (
            <>
              <div className="proposal-intro__greeting">Dear Valued Client,</div>
              <div className="proposal-section__body">{estimate.introduction}</div>
            </>
          )}

          {(estimate.objective || (estimate.objectives && estimate.objectives.length > 0)) && (
            <div style={{ marginTop: 24 }}>
              <div className="proposal-section__headline" style={{ fontSize: 22 }}>Objectives</div>
              <div className="proposal-section__accent-line" />
              {estimate.objective && (
                <div className="proposal-section__body" style={{ marginBottom: 12 }}>
                  {estimate.objective}
                </div>
              )}
              {estimate.objectives && estimate.objectives.length > 0 && (
                <div className="proposal-section__body">
                  {estimate.objectives.map((obj, i) => (
                    <div key={i} style={{ marginBottom: 6, paddingLeft: 16, position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0 }}>&bull;</span>
                      {typeof obj === 'string' ? obj : obj.text || obj.description || JSON.stringify(obj)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {estimate.areas && (
            <div style={{ marginTop: 24 }}>
              <div className="proposal-section__headline" style={{ fontSize: 22 }}>Areas of Service</div>
              <div className="proposal-section__accent-line" />
              <div className="proposal-section__body">{estimate.areas}</div>
            </div>
          )}

          <div className="proposal-intro__closing">
            <div>Sincerely,</div>
            {salesRepName && (
              <div className="proposal-intro__signature-name">{salesRepName}</div>
            )}
            {entityNameFormal && (
              <div className="proposal-intro__signature-title">{entityNameFormal}</div>
            )}
          </div>
        </div>
      </PageFrame>
    </div>
  )
}

/* ── Sub-line item helpers ────────────────────────────────── */
const calcSubTotal = (sub) => {
  // Always recalculate Labor/Equipment from components (stored totalPrice may be stale)
  if (sub.type === 'Equipment') {
    return (parseFloat(sub.qty) || 0) * (parseFloat(sub.pricePer) || 0)
  }
  if (sub.type === 'Labor') {
    const hours = (parseFloat(sub.qty) || 0) * (sub.crewSize || 1) * 8
    return hours * (parseFloat(sub.pricePer) || 0)
  }
  // For Materials, SubContractor, etc. trust stored totalPrice
  if (sub.totalPrice) return parseFloat(sub.totalPrice) || 0
  return (parseFloat(sub.qty) || 0) * (parseFloat(sub.pricePer) || 0)
}

const formatSubDescription = (sub) => {
  // If user set a display override, use it exclusively
  if (sub.displayDescription) return sub.displayDescription

  const showQty = sub.displayQty !== false
  const showSize = sub.displaySize !== false

  if (sub.type === 'Equipment') {
    const parts = [sub.description || sub.type]
    if (showQty && sub.qty) parts.push(`${sub.qty} day${sub.qty != 1 ? 's' : ''}`)
    return parts.join(' — ')
  }
  if (sub.type === 'Labor') {
    const parts = [sub.description || sub.type]
    if (showQty && sub.qty) parts.push(`${sub.qty} day${sub.qty != 1 ? 's' : ''}`)
    if (showQty && sub.crewSize > 1) parts.push(`${sub.crewSize} crew`)
    if (showQty && sub.totalHours) parts.push(`${sub.totalHours} hrs`)
    return parts.join(' — ')
  }
  if (sub.type === 'SubContractor') {
    // Show scope of work, never the vendor name
    return sub.displayDescription || 'Sub-Contractor'
  }
  if (sub.type === 'Materials') {
    const desc = sub.description || ''
    const sizeOrUnit = sub.size || sub.unit || ''
    const sizeAlreadyInDesc = sizeOrUnit && desc.toLowerCase().includes(sizeOrUnit.toLowerCase())
    let text = desc
    if (showQty && sub.qty) text += ` — Qty: ${sub.qty}`
    if (showSize && sizeOrUnit && !sizeAlreadyInDesc) text += ` (${sizeOrUnit})`
    return text
  }
  return sub.description || '\u2014'
}

/* ── Scope of Work Page ───────────────────────────────────── */
/* summaryPricing = true  → hide per-item pricing, show total */
/* summaryPricing = false → show all pricing + sub-item costs */
const ScopeOfWorkPage = ({ theme, estimate, entityNameFormal, pageNumber, totalPages }) => {
  const items = estimate.lineItems || estimate.scopeOfWork || []
  if (items.length === 0) return null

  const subtotal = estimate.totalPrice || 0
  const hidePricing = estimate.summaryPricing
  const showSubPricing = !hidePricing && estimate.showSubLineItemPricing

  return (
    <div className="proposal-page proposal-page--flow">
      <PageFrame theme={theme} pageNumber={pageNumber} totalPages={totalPages} entityNameFormal={entityNameFormal} headerTitle="Landscape Proposal">
        <div className="proposal-section">
          <div className="proposal-section__headline">Scope of Work</div>
          <div className="proposal-section__accent-line" />

          <table className="proposal-scope__table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit</th>
                {!hidePricing && <th>Price Per</th>}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const hasSubs = item.subLineItems && item.subLineItems.length > 0
                const rows = []

                // Main line item row
                rows.push(
                  <tr key={i}>
                    <td>
                      <div className="proposal-scope__service-name">
                        {item.description || '\u2014'}
                      </div>
                      {item.templatedDescription && (
                        <div className="proposal-scope__service-desc">
                          {item.templatedDescription}
                        </div>
                      )}
                    </td>
                    <td>{item.qty != null ? item.qty : '\u2014'}</td>
                    <td>{item.unit || '\u2014'}</td>
                    {!hidePricing && (
                      <td>{item.pricePer != null ? formatCurrency(item.pricePer) : '\u2014'}</td>
                    )}
                    <td>
                      {hidePricing ? '' : (item.totalPrice != null ? formatCurrency(item.totalPrice) : '\u2014')}
                    </td>
                  </tr>
                )

                // Sub-line item rows — each on its own table row, indented
                if (hasSubs) {
                  item.subLineItems.forEach((sub, j) => {
                    const subTotal = calcSubTotal(sub)
                    const isNote = sub.type === 'Notes' || sub.type === 'Measurements'

                    if (isNote) {
                      rows.push(
                        <tr key={`${i}-sub-${j}`} className="proposal-scope__sub-row">
                          <td colSpan={hidePricing ? 4 : 5} style={{ paddingLeft: 28 }}>
                            <div style={{
                              fontSize: 10,
                              color: 'var(--proposal-muted)',
                              lineHeight: 1.45,
                              padding: '4px 10px',
                              background: 'var(--proposal-bg)',
                              borderLeft: '2px solid var(--proposal-gold)',
                              borderRadius: '0 3px 3px 0',
                              fontStyle: 'normal',
                            }}>
                              {sub.displayDescription || sub.description || ''}
                            </div>
                          </td>
                        </tr>
                      )
                      return
                    }

                    // Split " - " delimited descriptions into header + detail lines
                    const descHasParts = sub.description && sub.description.includes(' - ')
                    if (descHasParts && (sub.type === 'Materials' || sub.type === 'Labor')) {
                      const parts = sub.description.split(' - ').map(s => s.trim()).filter(Boolean)
                      const headerText = parts[0]
                      const detailItems = parts.slice(1)

                      // Build header suffix based on type
                      let headerSuffix = ''
                      if (sub.type === 'Materials') {
                        const showQty = sub.displayQty !== false
                        const showSize = sub.displaySize !== false
                        const sizeOrUnit = sub.size || sub.unit || ''
                        const suffixParts = []
                        if (showQty && sub.qty) suffixParts.push(`Qty: ${sub.qty}`)
                        if (showSize && sizeOrUnit) suffixParts.push(`(${sizeOrUnit})`)
                        if (suffixParts.length) headerSuffix = ` — ${suffixParts.join(' ')}`
                      } else {
                        // Labor: show days, crew, hours
                        const suffixParts = []
                        if (sub.qty) suffixParts.push(`${sub.qty} day${sub.qty != 1 ? 's' : ''}`)
                        if (sub.crewSize > 1) suffixParts.push(`${sub.crewSize} crew`)
                        if (sub.totalHours) suffixParts.push(`${sub.totalHours} hrs`)
                        if (suffixParts.length) headerSuffix = ` — ${suffixParts.join(' — ')}`
                      }

                      // Header row with price
                      rows.push(
                        <tr key={`${i}-sub-${j}`} className="proposal-scope__sub-row">
                          <td style={{ paddingLeft: 28, fontSize: 11, color: 'var(--proposal-muted)' }}>
                            <span>&bull;&ensp;{headerText}{headerSuffix}</span>
                          </td>
                          <td></td>
                          <td></td>
                          {!hidePricing && <td></td>}
                          <td style={{ fontSize: 11, color: 'var(--proposal-muted)' }}>
                            {showSubPricing && subTotal > 0 ? formatCurrency(subTotal) : ''}
                          </td>
                        </tr>
                      )
                      // Detail rows — left-aligned in the description column only
                      detailItems.forEach((detail, k) => {
                        rows.push(
                          <tr key={`${i}-sub-${j}-d-${k}`} className="proposal-scope__sub-row">
                            <td style={{ paddingLeft: 42, fontSize: 10, color: 'var(--proposal-muted)', paddingTop: 1, paddingBottom: 1 }}>
                              - {detail}
                            </td>
                            <td></td>
                            <td></td>
                            {!hidePricing && <td></td>}
                            <td></td>
                          </tr>
                        )
                      })
                    } else {
                      rows.push(
                        <tr key={`${i}-sub-${j}`} className="proposal-scope__sub-row">
                          <td style={{ paddingLeft: 28, fontSize: 11, color: 'var(--proposal-muted)' }}>
                            <span>&bull;&ensp;{formatSubDescription(sub)}</span>
                          </td>
                          <td></td>
                          <td></td>
                          {!hidePricing && <td></td>}
                          <td style={{ fontSize: 11, color: 'var(--proposal-muted)' }}>
                            {showSubPricing && subTotal > 0 ? formatCurrency(subTotal) : ''}
                          </td>
                        </tr>
                      )
                    }
                  })
                }

                return rows
              })}
            </tbody>
            <tfoot>
              {estimate.taxable && estimate.taxAmount > 0 ? (
                <>
                  <tr>
                    <td colSpan={hidePricing ? 3 : 4} style={{ fontWeight: 600, borderBottom: '1px solid #eae7e1' }}>Subtotal</td>
                    <td style={{ borderBottom: '1px solid #eae7e1' }}>{formatCurrency(subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan={hidePricing ? 3 : 4} style={{ fontWeight: 600, borderBottom: '1px solid #eae7e1' }}>
                      Tax{getTaxRateDisplay(estimate) ? ` (${getTaxRateDisplay(estimate)})` : ''}
                    </td>
                    <td style={{ borderBottom: '1px solid #eae7e1' }}>{formatCurrency(estimate.taxAmount)}</td>
                  </tr>
                  <tr className="proposal-scope__total-row">
                    <td colSpan={hidePricing ? 3 : 4}>Total</td>
                    <td>{formatCurrency(subtotal + estimate.taxAmount)}</td>
                  </tr>
                </>
              ) : (
                <tr className="proposal-scope__total-row">
                  <td colSpan={hidePricing ? 3 : 4}>Total</td>
                  <td>{formatCurrency(subtotal)}<div style={{ fontSize: 9, fontWeight: 400, color: 'var(--proposal-muted)', marginTop: 2 }}>plus applicable taxes &amp; fees</div></td>
                </tr>
              )}
            </tfoot>
          </table>

          {hidePricing && (
            <div className="proposal-scope__notes">
              * Summary pricing shown — detailed line-item pricing available upon request.
            </div>
          )}
        </div>
      </PageFrame>
    </div>
  )
}

/* ── Project Details + Pricing Page ───────────────────────── */
const ProjectDetailsPage = ({ theme, estimate, client, jobSite, entityNameFormal, pageNumber, totalPages }) => {
  const subtotal = estimate.totalPrice || 0
  const taxAmount = estimate.taxAmount || 0
  const grandTotal = subtotal + taxAmount
  const depositPercent = estimate.depositPercent || 0
  const depositAmount = estimate.depositAmount || (depositPercent > 0 ? (grandTotal * depositPercent / 100) : 0)

  const details = []
  if (client?.displayName || client?.firstName) {
    details.push({ label: 'Client', value: client.displayName || client.firstName })
  }
  if (jobSite?.name || jobSite?.address) {
    details.push({ label: 'Job Site', value: jobSite.name || jobSite.address })
  }
  if (jobSite?.address && jobSite?.name) {
    details.push({ label: 'Address', value: jobSite.address })
  }
  if (estimate.created_at) {
    details.push({ label: 'Date', value: formatDate(estimate.created_at) })
  }
  if (estimate.duration) {
    details.push({ label: 'Est. Duration', value: `${estimate.duration} days` })
  }
  if (depositPercent > 0 && !estimate.waiveDeposit) {
    details.push({ label: `Deposit (${depositPercent}%)`, value: formatCurrency(depositAmount) })
  }
  if (estimate.dumpingAndHaulingFee > 0) {
    details.push({ label: 'Dumping & Hauling', value: formatCurrency(estimate.dumpingAndHaulingFee) })
  }
  if (estimate.supervisionFee > 0) {
    details.push({ label: 'Supervision Fee', value: formatCurrency(estimate.supervisionFee) })
  }

  return (
    <div className="proposal-page proposal-page--flow">
      <PageFrame theme={theme} pageNumber={pageNumber} totalPages={totalPages} entityNameFormal={entityNameFormal} headerTitle="Landscape Proposal">
        <div className="proposal-section">
          <div className="proposal-section__headline">Project Details</div>
          <div className="proposal-section__accent-line" />

          {details.length > 0 && (
            <div className="proposal-project__list">
              {details.map((item, idx) => (
                <div className="proposal-project__item" key={idx}>
                  <div className="proposal-project__item-label">{item.label}</div>
                  <div className="proposal-project__item-value">{item.value || '\u2014'}</div>
                </div>
              ))}
            </div>
          )}

          {grandTotal > 0 && (
            <div className="proposal-project__total">
              {estimate.taxable && taxAmount > 0 ? (
                <>
                  <div className="proposal-project__total-label">Total Estimate</div>
                  <div className="proposal-project__total-amount">
                    {formatCurrency(grandTotal)}
                  </div>
                  <div className="proposal-project__total-note">
                    Includes {formatCurrency(taxAmount)} in taxes{getTaxRateDisplay(estimate) ? ` (${getTaxRateDisplay(estimate)})` : ''}
                  </div>
                </>
              ) : (
                <>
                  <div className="proposal-project__total-label">Total Estimate</div>
                  <div className="proposal-project__total-amount">
                    {formatCurrency(grandTotal)}
                  </div>
                  <div className="proposal-project__total-note">
                    plus applicable taxes &amp; fees
                  </div>
                </>
              )}
              {depositPercent > 0 && !estimate.waiveDeposit && (
                <div className="proposal-project__total-note">
                  Deposit of {formatCurrency(depositAmount)} ({depositPercent}%) required to begin work
                </div>
              )}
            </div>
          )}

          <div className="proposal-signature">
            <div className="proposal-signature__row">
              <div className="proposal-signature__field">
                <div className="proposal-signature__line" />
                <div className="proposal-signature__label">Accepted</div>
              </div>
              <div className="proposal-signature__field">
                <div className="proposal-signature__line" />
                <div className="proposal-signature__label">Date</div>
              </div>
            </div>
          </div>
        </div>
      </PageFrame>
    </div>
  )
}

/* ── Notes Page (estimate-specific notes) ─────────────────── */
const NotesPage = ({ theme, estimate, entityNameFormal, pageNumber, totalPages }) => {
  const sections = []

  if (estimate.notes) {
    sections.push({ title: 'General Notes', content: estimate.notes })
  }
  if (estimate.proposalNotes && estimate.proposalNotes.length > 0) {
    const noteText = estimate.proposalNotes.map(n =>
      typeof n === 'string' ? n : n.text || n.note || JSON.stringify(n)
    ).join('\n')
    sections.push({ title: 'Proposal Notes', content: noteText })
  }
  if (estimate.paymentSchedule) {
    sections.push({ title: 'Payment Schedule', content: estimate.paymentSchedule })
  }
  if (estimate.disclosures && estimate.disclosures.length > 0) {
    const discText = estimate.disclosures.map(d =>
      typeof d === 'string' ? d : d.text || d.description || JSON.stringify(d)
    ).join('\n')
    sections.push({ title: 'Disclosures', content: discText })
  }
  if (estimate.finalParagraph) {
    sections.push({ title: null, content: estimate.finalParagraph })
  }
  if (estimate.closing) {
    sections.push({ title: 'Closing', content: estimate.closing })
  }

  if (sections.length === 0) return null

  return (
    <div className="proposal-page proposal-page--flow">
      <PageFrame theme={theme} pageNumber={pageNumber} totalPages={totalPages} entityNameFormal={entityNameFormal} headerTitle="Landscape Proposal">
        <div className="proposal-section">
          <div className="proposal-section__headline">Notes</div>
          <div className="proposal-section__accent-line" />
          {sections.map((section, idx) => (
            <div className="proposal-terms__section" key={idx}>
              {section.title && (
                <div className="proposal-terms__section-title">{section.title}</div>
              )}
              <div className="proposal-terms__section-content">{section.content}</div>
            </div>
          ))}
        </div>
      </PageFrame>
    </div>
  )
}

/* ── Terms & Conditions Page ──────────────────────────────── */
/* Uses the terms sections from the MTC proposal module       */
/* registry — same content the maintenance proposals show     */
const termsCompactStyles = {
  section: {
    marginBottom: 8,
  },
  title: {
    fontFamily: 'var(--proposal-font-display)',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--proposal-primary)',
    marginBottom: 2,
  },
  content: {
    fontFamily: 'var(--proposal-font-body)',
    fontSize: 9.5,
    color: 'var(--proposal-text)',
    lineHeight: 1.4,
  },
  headline: {
    fontSize: 24,
    marginBottom: 4,
  },
  sectionPadding: {
    padding: '24px 48px',
  },
}

const TermsConditionsPage = ({ theme, termsDefaults, variables, entityNameFormal, pageNumber, totalPages }) => {
  if (!termsDefaults || !termsDefaults.sections) return null

  const visibleSections = termsDefaults.sections.filter(s => !s.hidden)
  if (visibleSections.length === 0) return null

  return (
    <div className="proposal-page proposal-page--flow">
      <PageFrame theme={theme} pageNumber={pageNumber} totalPages={totalPages} entityNameFormal={entityNameFormal} headerTitle="Landscape Proposal">
        <div style={termsCompactStyles.sectionPadding}>
          <div className="proposal-section__headline" style={termsCompactStyles.headline}>
            {termsDefaults.headline || 'Terms & Conditions'}
          </div>
          <div className="proposal-section__accent-line" />
          {visibleSections.map((section, idx) => (
            <div key={idx} style={termsCompactStyles.section}>
              {section.title && (
                <div style={termsCompactStyles.title}>{section.title}</div>
              )}
              {section.content && (
                <div style={termsCompactStyles.content}>
                  {interpolate(section.content, variables)}
                </div>
              )}
            </div>
          ))}
        </div>
      </PageFrame>
    </div>
  )
}

/* ── Main Preview Page ────────────────────────────────────── */
const EstimatePreviewPage = ({ preloadedData }) => {
  const params = useParams()
  const location = useLocation()
  const isPdfMode = new URLSearchParams(location.search).get('pdf') === '1'
  const [data, setData] = useState(preloadedData || null)
  const [loading, setLoading] = useState(!preloadedData)
  const [error, setError] = useState(null)

  // Use templateTheme from the API (the real, customized colors from the MTC template)
  // Fall back to hardcoded themes only if the server didn't return a template
  const entity = data?.entity || data?.estimate?.entity || null
  const themeConfig = data?.templateTheme || getFallbackTheme(entity)
  const theme = useTheme(themeConfig)

  const entityKey = getEntityKey(entity)
  const entityNameFormal = (entity && typeof entity === 'object' && entity.entityNameFormal) ||
    ENTITY_NAME_MAP[entityKey] || 'Handyman Maintenance, Inc.'

  // Build variables for terms interpolation
  // Priority: estimate overrides > entity defaults > template variables > code defaults
  const codeDefaults = {}
  VARIABLE_DEFINITIONS.forEach(d => { codeDefaults[d.key] = d.defaultValue })
  const templateVariables = data?.templateVariables || {}
  const entityVariables = data?.entityVariables || {}
  const estimateVariables = data?.estimateVariables || {}
  const variables = {
    ...codeDefaults,
    companyName: (entity && typeof entity === 'object' && entity.name) || 'HMI',
    entityNameFormal,
    ...templateVariables,
    ...entityVariables,
    ...estimateVariables
  }

  useEffect(() => {
    if (preloadedData) return
    const id = params.id
    if (!id) return

    setLoading(true)
    axios.get(`/api/proposal/preview-data/${id}`)
      .then(res => {
        setData(res.data)
        setError(null)
      })
      .catch(err => {
        console.error('Failed to load estimate preview:', err)
        setError(err.response?.data?.message || 'Failed to load estimate')
      })
      .finally(() => setLoading(false))
  }, [params.id, preloadedData])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: "'DM Sans', sans-serif", fontSize: 18, color: '#666' }}>
        Loading estimate preview...
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: "'DM Sans', sans-serif", fontSize: 18, color: '#c62828' }}>
        {error}
      </div>
    )
  }

  if (!data || !data.estimate) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: "'DM Sans', sans-serif", fontSize: 18, color: '#666' }}>
        No estimate data
      </div>
    )
  }

  const { estimate, client, jobSite, salesRepName, termsDefaults } = data

  // Determine which pages are present
  const hasIntro = estimate.introduction || estimate.objective || (estimate.objectives && estimate.objectives.length) || estimate.areas
  const hasItems = (estimate.lineItems && estimate.lineItems.length > 0) || (estimate.scopeOfWork && estimate.scopeOfWork.length > 0)
  const hasNotes = estimate.notes || (estimate.proposalNotes && estimate.proposalNotes.length > 0) ||
    (estimate.disclosures && estimate.disclosures.length > 0) || estimate.paymentSchedule ||
    estimate.finalParagraph || estimate.closing
  const hasTerms = termsDefaults && termsDefaults.sections && termsDefaults.sections.filter(s => !s.hidden).length > 0

  // Count framed pages (everything except cover)
  let framedCount = 0
  if (hasIntro) framedCount++
  if (hasItems) framedCount++
  framedCount++ // project details always shows
  if (hasNotes) framedCount++
  if (hasTerms) framedCount++
  const totalPages = framedCount

  let pageNum = 0

  // PDF mode: use proposal-preview-only (hides PageFrame footer, enables page breaks)
  // Browser mode: use proposal-preview with background styling
  if (isPdfMode) {
    return (
      <div className="proposal-preview-only">
        <CoverPage theme={theme} estimate={estimate} client={client} salesRepName={salesRepName} entityNameFormal={entityNameFormal} />
        {hasIntro && <IntroductionPage theme={theme} estimate={estimate} entityNameFormal={entityNameFormal} salesRepName={salesRepName} pageNumber={0} totalPages={0} />}
        {hasItems && <ScopeOfWorkPage theme={theme} estimate={estimate} entityNameFormal={entityNameFormal} pageNumber={0} totalPages={0} />}
        <ProjectDetailsPage theme={theme} estimate={estimate} client={client} jobSite={jobSite} entityNameFormal={entityNameFormal} pageNumber={0} totalPages={0} />
        {hasNotes && <NotesPage theme={theme} estimate={estimate} entityNameFormal={entityNameFormal} pageNumber={0} totalPages={0} />}
        {hasTerms && <TermsConditionsPage theme={theme} termsDefaults={termsDefaults} variables={variables} entityNameFormal={entityNameFormal} pageNumber={0} totalPages={0} />}
      </div>
    )
  }

  return (
    <div style={{ background: '#d4d0c8', minHeight: '100vh', padding: '32px 0' }}>
      <div className="proposal-preview">
        {/* Cover — no frame */}
        <CoverPage
          theme={theme}
          estimate={estimate}
          client={client}
          salesRepName={salesRepName}
          entityNameFormal={entityNameFormal}
        />

        {/* Introduction */}
        {hasIntro && (
          <IntroductionPage
            theme={theme}
            estimate={estimate}
            entityNameFormal={entityNameFormal}
            salesRepName={salesRepName}
            pageNumber={++pageNum}
            totalPages={totalPages}
          />
        )}

        {/* Scope of Work */}
        {hasItems && (
          <ScopeOfWorkPage
            theme={theme}
            estimate={estimate}
            entityNameFormal={entityNameFormal}
            pageNumber={++pageNum}
            totalPages={totalPages}
          />
        )}

        {/* Project Details + Pricing + Signature */}
        <ProjectDetailsPage
          theme={theme}
          estimate={estimate}
          client={client}
          jobSite={jobSite}
          entityNameFormal={entityNameFormal}
          pageNumber={++pageNum}
          totalPages={totalPages}
        />

        {/* Notes (estimate-specific) */}
        {hasNotes && (
          <NotesPage
            theme={theme}
            estimate={estimate}
            entityNameFormal={entityNameFormal}
            pageNumber={++pageNum}
            totalPages={totalPages}
          />
        )}

        {/* Terms & Conditions (from MTC template module registry) */}
        {hasTerms && (
          <TermsConditionsPage
            theme={theme}
            termsDefaults={termsDefaults}
            variables={variables}
            entityNameFormal={entityNameFormal}
            pageNumber={++pageNum}
            totalPages={totalPages}
          />
        )}
      </div>
    </div>
  )
}

export default EstimatePreviewPage
